import { FC, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import type { SeopMonth, SeopScenario } from '@/types/seop';
import { mesDoCenario } from '@/utils/seopCenario';
import { seopMesComJanela } from '@/utils/seopFormat';

interface CorrigirMesProps {
  /** O cenário cujo mês está sendo corrigido; `null` = modal fechado. */
  cenario: SeopScenario | null;
  months: SeopMonth[];
  isBusy: boolean;
  onConfirmar: (mes: SeopMonth) => void;
  onCancelar: () => void;
}

const Modal: FC<CorrigirMesProps & { cenario: SeopScenario }> = ({ cenario, months, isBusy, onConfirmar, onCancelar }) => {
  // Só os outros meses do plano: corrigir para o mês em que o cenário já está não é correção.
  const opcoes = months.filter((m) => m.id !== cenario.month?.id);
  const [escolhido, setEscolhido] = useState(opcoes[0]?.id ?? '');
  const alvo = opcoes.find((m) => m.id === escolhido);
  return (
    <ConfirmModal
      open
      title={`Corrigir o mês de "${cenario.name}"?`}
      confirmLabel="Corrigir mês"
      tone="accent"
      isBusy={isBusy}
      onConfirm={() => alvo && onConfirmar(alvo)}
      onCancel={onCancelar}
    >
      <p>
        Use quando o cenário foi salvo no mês errado. Hoje ele está em <strong>{mesDoCenario(cenario, months)}</strong>.
      </p>
      <p>
        Os números do cenário não mudam, e o plano atual também não. Muda só o mês a que o cenário pertence: é nesse mês
        que ele abre e é nesse mês que ele entra quando vira o plano atual.
      </p>
      {opcoes.length === 0 ? (
        <p data-testid="seop-cenario-sem-outro-mes">
          Não há outro mês no plano. Adicione o mês em Cadastros → Meses do plano e volte aqui.
        </p>
      ) : (
        <label className="mt-3 block text-xs text-content-muted">
          Este cenário é de
          <select
            aria-label="Mês do cenário"
            value={escolhido}
            onChange={(e) => setEscolhido(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm text-content-primary"
          >
            {opcoes.map((m) => (
              <option key={m.id} value={m.id}>
                {seopMesComJanela(m)}
              </option>
            ))}
          </select>
        </label>
      )}
    </ConfirmModal>
  );
};

/**
 * Corrigir o mês de um cenário salvo no mês errado.
 *
 * Nasceu em 02/10/2026: os cenários "Outubro V1/V2" tinham sido salvos no mês que depois
 * se revelou ser o de novembro, e corrigir o mês do PLANO os levou junto para novembro.
 * O destino é sempre um mês que já existe no plano — é lá que o cenário vai abrir e entrar.
 */
const CorrigirMesDoCenario: FC<CorrigirMesProps> = ({ cenario, ...resto }) =>
  // `key`: trocar de cenário recomeça a escolha do mês.
  cenario ? <Modal key={cenario.id} cenario={cenario} {...resto} /> : null;

export default CorrigirMesDoCenario;
